import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import propertiesData from "../data/private/properties.json";
import locationData from "../data/private/location.json";
import flowGuidanceData from "../data/private/flowGuidance.json";
import { logActivity, completeProgressStep } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import CountryCitySelect from "../components/CountryCitySelect";
import FlowGuidanceBanner from "../components/FlowGuidanceBanner";
import useModalBehavior from "../utils/useModalBehavior";
import { createProperty, deleteProperty, updateProperty } from "../api/properties.api";
import { useClients } from "../context/ClientsContext";
import { useProperties } from "../context/PropertiesContext";
import { markSelectionUpdated } from "../utils/debugState";
import {
  clearStoredProjectId,
  extractProjectIdFromProperty,
  persistProjectId,
} from "../utils/projectId";

export default function DashboardProperties() {
  const notifySelectionUpdated = () => {
    if (typeof window === "undefined") return;
    markSelectionUpdated("property");
    window.dispatchEvent(new Event("myv_selection_updated"));
  };
  const syncProjectId = useCallback((property) => {
    const projectId = extractProjectIdFromProperty(property);
    if (projectId) {
      persistProjectId(projectId);
    } else {
      clearStoredProjectId();
    }
  }, []);
  const [showModal, setShowModal] = useState(false);
  const { clients } = useClients();
  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
    refreshProperties,
    upsertProperty,
    removeProperty,
  } = useProperties();
  const allProperties = Array.isArray(properties) ? properties : [];
  const [toast, setToast] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [dismissed, setDismissed] = useState(
    Boolean(localStorage.getItem("myv_guidance_properties"))
  );
  const [activeProperty, setActiveProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [form, setForm] = useState({
    name: "",
    client: "",
    type: "",
    city: "",
    notes: "",
  });
  const modalRef = useRef(null);
  const handleClose = useCallback(() => {
    setShowModal(false);
    setEditingProperty(null);
    setForm({
      name: "",
      client: "",
      type: "",
      city: "",
      notes: "",
    });
  }, []);
  const selectedClientName = localStorage.getItem("myv_selected_client") || "";
  const selectedClientId =
    localStorage.getItem("myv_selected_client_id") || "";
  const clientNameById = useMemo(
    () =>
      new Map(
        clients.map((client) => [client.id, client.data?.name || ""])
      ),
    [clients]
  );
  const resolveClientId = useCallback(
    (clientRef) => {
      if (!clientRef) return "";
      const direct = clients.find((client) => client.id === clientRef);
      if (direct?.id) return direct.id;
      const byName = clients.find((client) => client.data?.name === clientRef);
      return byName?.id || clientRef;
    },
    [clients]
  );
  const resolveClientLabel = useCallback(
    (property) => {
    const clientRef = property?.data?.client;
      if (!clientRef) return "—";
      return clientNameById.get(clientRef) || clientRef;
    },
    [clientNameById]
  );
  const filteredItems = useMemo(() => {
    const rawItems = Array.isArray(allProperties) ? allProperties : [];
    if (!selectedClientId && !selectedClientName) {
      return rawItems;
    }
    return rawItems.filter((item) => {
      const clientRef = item?.data?.client;
      return (
        clientRef === selectedClientId ||
        clientRef === selectedClientName
      );
    });
  }, [allProperties, selectedClientId, selectedClientName]);
  const hasProperties = filteredItems.length > 0;
  const selectedClient = localStorage.getItem("myv_selected_client");

  useEffect(() => {
    const shouldOpen = localStorage.getItem("myv_action_add_property");
    if (shouldOpen) {
      setShowModal(true);
      localStorage.removeItem("myv_action_add_property");
    }
  }, []);

  useEffect(() => {
    if (propertiesLoading) return;
    const stored = localStorage.getItem("myv_selected_property");
    const nextActive =
      allProperties.find((item) => item.data?.name === stored) ||
      allProperties[0] ||
      null;
    setActiveProperty(nextActive);
    syncProjectId(nextActive);
    logActivity("property_loaded", { clientId: selectedClientId || "" });
  }, [allProperties, propertiesLoading, selectedClientId, syncProjectId]);

  useEffect(() => {
    const stored = localStorage.getItem("myv_selected_property");
    const nextActive =
      filteredItems.find((item) => item.data?.name === stored) ||
      filteredItems[0] ||
      null;
    setActiveProperty(nextActive);
    syncProjectId(nextActive);
  }, [filteredItems, syncProjectId]);

  useEffect(() => {
    setLoadError(propertiesError ? normalizeApiError(propertiesError) : null);
  }, [propertiesError]);

  useEffect(() => {
    const handleAction = (event) => {
      if (event.detail === "add-property") {
        setShowModal(true);
        localStorage.removeItem("myv_action_add_property");
      }
    };
    window.addEventListener("myv_command_action", handleAction);
    return () =>
      window.removeEventListener("myv_command_action", handleAction);
  }, []);

  useModalBehavior(showModal, handleClose, modalRef);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">
          {propertiesData.title}
        </h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingProperty(null);
            setForm({
              name: "",
              client: "",
              type: "",
              city: "",
              notes: "",
            });
            setShowModal(true);
            logActivity("properties_add_open");
          }}
        >
          {propertiesData.ctaLabel}
        </button>
      </div>

      {!selectedClient && (
        <FlowGuidanceBanner
          eventKey="properties-missing-client"
          title={flowGuidanceData.properties.missingClient.title}
          description={flowGuidanceData.properties.missingClient.description}
          meta={{ page: "properties", missing: "client" }}
        />
      )}

      {hasProperties ? (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredItems.map((item) => (
            <div
              key={item.id || item.data?.name}
              className={`card card-hover p-4 ${
                activeProperty?.id === item.id
                  ? "border-amber-500 text-slate-900 shadow-md"
                  : "text-slate-600"
              }`}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
              onClick={async () => {
                if (item?.data?.name) {
                  localStorage.setItem("myv_selected_property", item.data.name);
                }
                setActiveProperty(item);
                  syncProjectId(item);
                if (item.id) {
                  localStorage.setItem("myv_selected_property_id", item.id);
                }
                notifySelectionUpdated();
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">
                  {item.data?.name || "—"}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className="text-amber-600 hover:text-amber-700"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingProperty(item);
                      setForm({
                        name: item.data?.name || "",
                        client: resolveClientId(item.data?.client),
                        type: item.data?.type || "",
                        city: item.data?.city || "",
                        notes: item.data?.notes || "",
                      });
                      try {
                        localStorage.setItem(
                          "myv_property_location",
                          JSON.stringify({
                            country: item.data?.country || "",
                            city: item.data?.city || "",
                          })
                        );
                      } catch (error) {
                        // no-op
                      }
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-rose-600 hover:text-rose-700"
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (!item?.id) return;
                      const confirmed = window.confirm(
                        `Delete property ${item.data?.name || ""}?`
                      );
                      if (!confirmed) return;
                      try {
                        await deleteProperty(item.id);
                        removeProperty(item.id);
                        if (activeProperty?.id === item.id) {
                          setActiveProperty(null);
                        }
                        setToast("Property deleted");
                      } catch (error) {
                        setToast(
                          error?.response?.data?.message || error?.message || ""
                        );
                        logActivity("api_error", { module: "properties" });
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-2 grid gap-1 text-xs text-slate-500">
                <p>ID: {item.id || "—"}</p>
                <p>Type: {item.data?.type || "—"}</p>
                <p>Notes: {item.data?.notes || "—"}</p>
                <p>Client: {resolveClientLabel(item)}</p>
                <p>City: {item.data?.city || "—"}</p>
                <p>Country: {item.data?.country || "—"}</p>
                <p>Created: {item.createdAt || "—"}</p>
                <p>Updated: {item.updatedAt || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No properties found"
          message="Add a property to continue."
          testId="dashboard-empty-properties"
        />
      )}

      {loadError && <ErrorState error={loadError} onRetry={refreshProperties} />}

      {!dismissed && (
        <div className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {propertiesData.guidance.title}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {propertiesData.guidance.description}
              </p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => {
                localStorage.setItem("myv_guidance_properties", "true");
                setDismissed(true);
                logActivity("guidance_dismissed", { page: "properties" });
              }}
            >
              {propertiesData.guidance.ctaLabel}
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              handleClose();
              logActivity("properties_add_close");
            }}
          >
            <motion.div
              ref={modalRef}
              className="card modal-panel w-full max-w-md p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dashboard-property-modal-title"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
            >
              <h2
                id="dashboard-property-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                {editingProperty ? "Edit Property" : propertiesData.modal.title}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {propertiesData.modal.subtitle}
              </p>
              <div className="mt-4 grid gap-3">
                {propertiesData.modal.fields.map((field) => (
                  <div key={field.key}>
                    <label className="text-xs text-slate-500">
                      {field.label}
                    </label>
                    {field.key === "client" ? (
                      <select
                        className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        value={form.client}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            client: event.target.value,
                          }))
                        }
                      >
                        <option value="">{field.placeholder}</option>
                        {clients.map((client) => (
                          <option
                            key={client.id || client.data?.name}
                            value={client.id}
                          >
                            {client.data?.name || ""}
                          </option>
                        ))}
                      </select>
                    ) : field.key === "type" ? (
                      <select
                        className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        value={form.type}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            type: event.target.value,
                          }))
                        }
                      >
                        <option value="">{field.placeholder}</option>
                        {propertiesData.modal.propertyTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        placeholder={field.placeholder}
                        value={form[field.key]}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            [field.key]: event.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <CountryCitySelect
                  storageKey="myv_property_location"
                  labels={locationData.propertyModal}
                />
              </div>
              <button
                className="btn btn-primary mt-4 w-full"
                onClick={async () => {
                  const storedLocation = (() => {
                    try {
                      const raw = localStorage.getItem("myv_property_location");
                      return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                      return {};
                    }
                  })();
                  const payload = {
                    name: form.name,
                    client: form.client,
                    type: form.type,
                    notes: form.notes,
                    city: form.city || storedLocation.city || "",
                    country: storedLocation.country || "",
                  };
                  try {
                    if (editingProperty?.id) {
                      await updateProperty(editingProperty.id, payload);
                      upsertProperty({ id: editingProperty.id, data: payload });
                      setActiveProperty((prev) =>
                        prev?.id === editingProperty.id
                          ? { ...prev, data: { ...prev.data, ...payload } }
                          : prev
                      );
                      setToast("Property updated");
                      logActivity("property_updated", { name: payload.name });
                    } else {
                      await createProperty(payload);
                      const fresh = await refreshProperties();
                      const rawItems = Array.isArray(fresh?.items) ? fresh.items : [];
                      const nextActive =
                        rawItems.find((item) => item.data?.name === form.name) ||
                        rawItems[0] ||
                        null;
                      setActiveProperty(nextActive);
                      setToast(propertiesData.toastMessage);
                      logActivity("property_added", { name: form.name });
                      completeProgressStep("property_added");
                      if (nextActive?.data?.name) {
                        localStorage.setItem(
                          "myv_selected_property",
                          nextActive.data.name
                        );
                        notifySelectionUpdated();
                      }
                    }
                    setForm({
                      name: "",
                      client: "",
                      type: "",
                      city: "",
                      notes: "",
                    });
                    setEditingProperty(null);
                    handleClose();
                    setLoadError(null);
                  } catch (error) {
                    logActivity("api_error", { module: "properties" });
                    setLoadError(normalizeApiError(error));
                    setToast(error?.response?.data?.message || error?.message || "");
                  }
                }}
              >
                {editingProperty
                  ? "Update Property"
                  : propertiesData.modal.submitLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {toast && (
        <div className="card p-4 text-sm text-slate-600">{toast}</div>
      )}
    </div>
  );
}
